import React, { useState, useEffect, useRef } from 'react';
import { CheckIcon, ChevronUpDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Combobox } from '@headlessui/react';

export interface NafCode {
  code: string;
  intitule: string;
}

interface NafCodeSelectorProps {
  nafCodes: NafCode[];
  selectedNafCodes: string[];
  onChange: (codes: string[]) => void;
  disabled?: boolean;
}

const NafCodeSelector: React.FC<NafCodeSelectorProps> = ({
  nafCodes,
  selectedNafCodes,
  onChange,
  disabled = false
}) => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  
  const filteredNafCodes = query === ''
    ? nafCodes
    : nafCodes.filter((nafCode) => {
        const searchTerm = query.toLowerCase();
        return (
          nafCode.code.toLowerCase().includes(searchTerm) ||
          nafCode.intitule.toLowerCase().includes(searchTerm)
        );
      });

  const handleNafCodeSelect = (code: string) => {
    // Toggle selection - if already selected, remove it
    if (selectedNafCodes.includes(code)) {
      onChange(selectedNafCodes.filter(c => c !== code));
    } else {
      // If not selected, add it
      onChange([...selectedNafCodes, code]);
    }
    // Keep the dropdown open
    setIsOpen(true);
  };

  const removeNafCode = (codeToRemove: string) => {
    onChange(selectedNafCodes.filter(code => code !== codeToRemove));
  };

  // Add click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        // Reset query when closing to ensure fresh results on reopen
        setQuery('');
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Create a custom onChange handler that doesn't close the dropdown
  const handleComboboxChange = (value: string) => {
    handleNafCodeSelect(value);
    // Override the default behavior which would close the dropdown
    setTimeout(() => {
      setIsOpen(true);
    }, 0);
  };

  // Toggle dropdown with proper state reset
  const toggleDropdown = () => {
    if (isOpen) {
      setIsOpen(false);
      setQuery('');
    } else {
      setIsOpen(true);
      // Focus the input when opening
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 0);
    }
  };

  return (
    <div ref={containerRef}>
      <label htmlFor="nafCode" className="block text-sm font-medium text-gray-700 mb-1">
        Code NAF
      </label>
      <Combobox
        as="div"
        value=""
        onChange={handleComboboxChange}
        disabled={disabled}
        className="relative mt-1"
      >
        <div className="relative">
          <Combobox.Input
            id="nafCode"
            ref={inputRef}
            className="w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:text-sm"
            onChange={(e) => {
              setQuery(e.target.value);
              if (e.target.value) {
                setIsOpen(true);
              }
            }}
            onFocus={() => setIsOpen(true)}
            placeholder="Rechercher un code NAF..."
            autoComplete="off"
            value={query}
          />
          <Combobox.Button 
            className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none"
            onClick={(e) => {
              e.preventDefault();
              toggleDropdown();
            }}
          >
            <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
          </Combobox.Button>
        </div>

        {filteredNafCodes.length > 0 && isOpen && (
          <Combobox.Options
            static
            className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
          >
            {filteredNafCodes.map((nafCode) => (
              <Combobox.Option
                key={nafCode.code}
                value={nafCode.code}
                className={({ active }) =>
                  `relative cursor-default select-none py-2 pl-3 pr-9 ${
                    active ? 'bg-blue-600 text-white' : 'text-gray-900'
                  }`
                }
              >
                {({ active }) => (
                  <>
                    <div className="flex">
                      <span className={`font-medium ${active ? 'text-white' : ''}`}>
                        {nafCode.code}
                      </span>
                      <span className={`ml-2 truncate ${active ? 'text-white' : 'text-gray-500'}`}>
                        {nafCode.intitule}
                      </span>
                    </div>

                    {selectedNafCodes.includes(nafCode.code) && (
                      <span
                        className={`absolute inset-y-0 right-0 flex items-center pr-4 ${
                          active ? 'text-white' : 'text-blue-600'
                        }`}
                      >
                        <CheckIcon className="h-5 w-5" aria-hidden="true" />
                      </span>
                    )}
                  </>
                )}
              </Combobox.Option>
            ))}
          </Combobox.Options>
        )}
      </Combobox>
      
      {/* Display selected NAF codes as badges/chips */}
      {selectedNafCodes.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {selectedNafCodes.map(code => {
            const nafCode = nafCodes.find(c => c.code === code);
            return (
              <div 
                key={code}
                className="inline-flex items-center bg-blue-50 text-blue-700 px-2.5 py-0.5 rounded-md text-sm font-medium"
              >
                <span className="mr-1 font-semibold">{code}</span>
                {nafCode && 
                  <span className="mr-1 text-blue-600">- {nafCode.intitule}</span>
                }
                <button
                  type="button"
                  className="ml-0.5 inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full text-blue-400 hover:bg-blue-200 hover:text-blue-600 focus:bg-blue-500 focus:text-white focus:outline-none"
                  onClick={() => removeNafCode(code)}
                  disabled={disabled}
                >
                  <span className="sr-only">Retirer ce code NAF</span>
                  <XMarkIcon className="h-3 w-3" aria-hidden="true" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default NafCodeSelector; 